import React, { memo } from 'react';
import { formatBR, isDateBefore, diffDays } from '@/lib/utils/dateUtils';
import { MoreHorizontal, Eye, Edit, Trash2, Receipt, ArrowUpRight, ArrowDownRight, Clock, CheckCircle, AlertCircle, XCircle, Repeat } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Skeleton } from '../ui/skeleton';
import { 
  AccrualTransaction,
  AccrualStatus,
  ACCRUAL_STATUS_COLORS,
  ACCRUAL_STATUS_LABELS,
  formatCurrency
} from '../../types/accrual';
import { cn } from '@/lib/utils';

interface AccrualTableProps {
  data: AccrualTransaction[];
  onSelectTransaction: (transactionId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  selectedTransactions: Set<string>;
  loading?: boolean;
  sortable?: boolean;
  onTransactionClick?: (transactionId: string) => void;
}

const AccrualTableComponent = ({
  data,
  onSelectTransaction,
  onSelectAll,
  selectedTransactions,
  loading,
  sortable = true,
  onTransactionClick
}: AccrualTableProps) => {
  
  const allSelected = data.length > 0 && data.every(transaction => selectedTransactions.has(transaction.id));
  const someSelected = data.some(transaction => selectedTransactions.has(transaction.id));

  const getStatusIcon = (status: AccrualStatus) => {
    switch (status) {
      case AccrualStatus.PAID:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case AccrualStatus.PENDING:
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case AccrualStatus.OVERDUE:
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case AccrualStatus.PARTIALLY_PAID:
        return <ArrowUpRight className="h-4 w-4 text-blue-600" />;
      case AccrualStatus.CANCELED:
        return <XCircle className="h-4 w-4 text-foreground" />;
      default:
        return <Clock className="h-4 w-4 text-foreground" />;
    }
  };

  const getTypeIcon = (type: 'receita' | 'despesa') => {
    return type === 'receita' 
      ? <ArrowUpRight className="h-4 w-4 text-green-600" />
      : <ArrowDownRight className="h-4 w-4 text-red-600" />;
  };

  const handleRowAction = (action: string, transaction: AccrualTransaction) => {
    console.log(`Action: ${action}`, transaction);
    // In a real application, these would trigger actual functionality
  };

  const isOverdue = (transaction: AccrualTransaction) => {
    if (transaction.status === AccrualStatus.PAID) return false;
    return isDateBefore(transaction.due_date, new Date());
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Lançamentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Lançamentos por Competência</CardTitle>
            <CardDescription>
              {data.length} lançamento(s) encontrado(s)
              {selectedTransactions.size > 0 && ` • ${selectedTransactions.size} selecionado(s)`}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Nenhum lançamento encontrado para os filtros aplicados.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={onSelectAll}
                      className={someSelected && !allSelected ? 'data-[state=checked]:bg-muted' : ''}
                    />
                  </TableHead>
                  <TableHead className="min-w-[200px]">Descrição</TableHead>
                  <TableHead className="w-24">Tipo</TableHead>
                  <TableHead className="w-32">Categoria</TableHead>
                  <TableHead className="w-32">Valor</TableHead>
                  <TableHead className="w-32">Competência</TableHead>
                  <TableHead className="w-32">Vencimento</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-40">Cliente/Fornecedor</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((transaction) => (
                  <TableRow 
                    key={transaction.id}
                    className={cn(
                      "hover:bg-muted/50",
                      selectedTransactions.has(transaction.id) && "bg-muted/50",
                      isOverdue(transaction) && transaction.status !== AccrualStatus.PAID && "border-l-4 border-l-red-500",
                      onTransactionClick && "cursor-pointer"
                    )}
                    onClick={() => {
                      if (onTransactionClick) {
                        onTransactionClick(transaction.id);
                      }
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedTransactions.has(transaction.id)}
                        onCheckedChange={(checked) => onSelectTransaction(transaction.id, !!checked)}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{transaction.description}</div>
                        <div className="flex items-center gap-2">
                          {transaction.document_number && (
                            <Badge variant="outline" className="text-xs">
                              <Receipt className="h-3 w-3 mr-1" />
                              {transaction.document_number}
                            </Badge>
                          )}
                          {transaction.is_recurring && (
                            <Badge variant="outline" className="text-xs">
                              <Repeat className="h-3 w-3 mr-1" />
                              {transaction.recurring_frequency === 'monthly' ? 'Mensal' :
                               transaction.recurring_frequency === 'quarterly' ? 'Trimestral' :
                               'Anual'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(transaction.type)}
                        <span className={cn(
                          "text-sm font-medium",
                          transaction.type === 'receita' ? 'text-green-600' : 'text-red-600'
                        )}>
                          {transaction.type === 'receita' ? 'Receita' : 'Despesa'}
                        </span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-xs min-w-[5rem] justify-center ${
                          transaction.type === 'receita' || transaction.category.toLowerCase().includes('receita')
                            ? 'bg-blue-medium-100 text-blue-medium-700 border-blue-medium-200'
                            : transaction.type === 'despesa' || transaction.category.toLowerCase().includes('despesa')
                            ? 'bg-blue-dark-100 text-blue-dark-600 border-blue-dark-200'
                            : 'bg-blue-light-100 text-blue-light-700 border-blue-light-200'
                        }`}
                      >
                        {transaction.category}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-right">
                        <div className={cn(
                          "font-medium",
                          transaction.type === 'receita' ? 'text-green-600' : 'text-red-600'
                        )}>
                          {formatCurrency(Math.abs(transaction.amount))}
                        </div>
                        {transaction.installment_number && transaction.total_installments && (
                          <div className="text-xs text-muted-foreground">
                            {transaction.installment_number}/{transaction.total_installments}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {formatBR(transaction.competence_date)}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className={cn(
                        "text-sm",
                        isOverdue(transaction) && transaction.status !== AccrualStatus.PAID && "text-red-600 font-medium"
                      )}>
                        {formatBR(transaction.due_date)}
                        {isOverdue(transaction) && transaction.status !== AccrualStatus.PAID && (
                          <div className="text-xs text-red-600">
                            {Math.abs(diffDays(new Date(), transaction.due_date))} dias
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(transaction.status)}
                        <Badge variant="outline" className={ACCRUAL_STATUS_COLORS[transaction.status]}>
                          {ACCRUAL_STATUS_LABELS[transaction.status]}
                        </Badge>
                        {transaction.payment_date && (
                          <div className="text-xs text-muted-foreground">
                            Pago em {formatBR(transaction.payment_date)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-sm">
                        {transaction.client_name || transaction.supplier_name || '-'}
                        {transaction.payment_method && (
                          <div className="text-xs text-muted-foreground">
                            {transaction.payment_method}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleRowAction('view', transaction)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRowAction('edit', transaction)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {transaction.status === AccrualStatus.PENDING && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleRowAction('mark-paid', transaction)}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Marcar como Pago
                              </DropdownMenuItem>
                            </>
                          )}
                          {transaction.status === AccrualStatus.PAID && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleRowAction('mark-pending', transaction)}
                                className="text-yellow-600"
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                Marcar como Pendente
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleRowAction('delete', transaction)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Memoized AccrualTable to prevent unnecessary re-renders
 */
export const AccrualTable = memo(AccrualTableComponent);